import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { EditorModule } from '@tinymce/tinymce-angular';
import { WebsiteService, Page, SupportedLanguage, LanguageOption, PageComponent, ComponentData } from '../../../services/website.service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationComponent } from '../../../components/notification/notification.component';
import { ConfirmModalComponent } from '../../../components/confirm-modal/confirm-modal.component';
import { TextComponentComponent } from './components/text-component/text-component.component';
import { ProductsComponentComponent } from './components/products-component/products-component.component';
import { ProductSelectorModalComponent } from './components/product-selector-modal/product-selector-modal.component';
import { MediaSelectorModalComponent } from './components/media-selector-modal/media-selector-modal.component';
import { GridComponentComponent } from './components/grid-component/grid-component.component';
import { MediaGalleryComponentComponent } from './components/media-gallery-component/media-gallery-component.component';
import { NewsComponentComponent } from './components/news-component/news-component.component';
import { ProjectsComponentComponent } from './components/projects-component/projects-component.component';
import { ProjectSelectorModalComponent } from './components/project-selector-modal/project-selector-modal.component';
import { FormComponentComponent } from './components/form-component/form-component.component';
import { MapComponentComponent } from './components/map-component/map-component.component';
import { HtmlBlockComponentComponent } from './components/html-block-component/html-block-component.component';
import { OnboardingTourComponent, TourStep } from './onboarding-tour/onboarding-tour.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

interface Category {
  id: string;
  name: string;
}

@Component({
  selector: 'app-page-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EditorModule,
    NotificationComponent,
    ConfirmModalComponent,
    TextComponentComponent,
    ProductsComponentComponent,
    ProductSelectorModalComponent,
    MediaSelectorModalComponent,
    GridComponentComponent,
    MediaGalleryComponentComponent,
    NewsComponentComponent,
    ProjectsComponentComponent,
    ProjectSelectorModalComponent,
    FormComponentComponent,
    MapComponentComponent,
    HtmlBlockComponentComponent,
    OnboardingTourComponent
  ],
  templateUrl: './page-editor.component.html',
  styleUrls: ['./page-editor.component.scss']
})
export class PageEditorComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private saveTimer: any = null;

  currentLocale: SupportedLanguage = 'en';
  currentPage: Page | null = null;

  /**
   * Vercel preview URL for the current page, scoped to the active domain.
   * Homepage uses '/', other pages use their route slug.
   * ?domain= triggers cache bypass on the frontend.
   */
  get previewUrl(): string | null {
    if (!this.currentPage) return null;
    const domain = this.websiteService.getCurrentDomain();
    if (!domain) return null;
    const isHomepage = this.currentPage.isHomepage;
    const route = isHomepage ? '' : (this.currentPage.route || '');
    if (!isHomepage && !route) return null;
    const path = route ? `/${encodeURIComponent(route)}` : '/';
    return `${environment.previewBaseUrl}${path}?domain=${encodeURIComponent(domain)}`;
  }

  availableLanguages: LanguageOption[] = [];
  selectedCategories: string[] = [];
  pageId: string = '';

  editorConfig = {
    base_url: '/tinymce',
    suffix: '.min',
    promotion: false,
    skin: 'oxide-dark',
    content_css: 'dark',
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount',
      'quickbars'
    ],
    toolbar: 'undo redo | blocks | ' +
      'bold italic forecolor | alignleft aligncenter ' +
      'alignright alignjustify | bullist numlist outdent indent | ' +
      'image media | styleselect | help',
    
    image_advtab: true,
    image_class_list: [
      { title: 'None', value: '' },
      { title: 'Responsive', value: 'img-fluid' },
      { title: 'Thumbnail', value: 'img-thumbnail' },
      { title: 'Left with text wrap', value: 'float-left' },
      { title: 'Right with text wrap', value: 'float-right' },
      { title: 'Center block', value: 'center-block' },
      { title: 'Full width', value: 'full-width' }
    ],
    image_dimensions: true,
    image_caption: true,
    
    quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
    quickbars_insert_toolbar: 'image media quicktable',
    
    image_toolbar: 'alignleft aligncenter alignright',
    
    content_style: `
      body {
        background-color: #1F2937;
        color: #D1D5DB;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        margin: 1rem;
      }
      img {
        max-width: 100%;
        height: auto;
        padding: 5px;
      }
      img.float-left {
        float: left;
        margin: 0 20px 20px 0;
        max-width: 50%;
      }
      img.float-right {
        float: right;
        margin: 0 0 20px 20px;
        max-width: 50%;
      }
      img.center-block {
        display: block;
        margin: 20px auto;
      }
      img.full-width {
        width: 100%;
        height: auto;
        margin: 20px 0;
      }
      .img-fluid {
        max-width: 100%;
        height: auto;
      }
      .img-thumbnail {
        padding: 0.25rem;
        background-color: #374151;
        border: 1px solid #4B5563;
        border-radius: 0.25rem;
        max-width: 100%;
        height: auto;
      }
      /* Clear fix for text wrap */
      .clearfix::after {
        content: "";
        clear: both;
        display: table;
      }
      p, div, span, li {
        color: #D1D5DB;
      }
      h1, h2, h3, h4, h5, h6 {
        color: #F3F4F6;
      }
      a {
        color: #60A5FA;
      }
      table {
        border-color: #374151;
      }
      td, th {
        border-color: #374151;
        color: #D1D5DB;
      }
      blockquote {
        border-left: 4px solid #4B5563;
        margin: 0;
        padding-left: 1rem;
        color: #9CA3AF;
      }
    `,
    style_formats: [
      {
        title: 'Image Styles',
        items: [
          { title: 'Float Left', selector: 'img', classes: 'float-left' },
          { title: 'Float Right', selector: 'img', classes: 'float-right' },
          { title: 'Center Block', selector: 'img', classes: 'center-block' },
          { title: 'Full Width', selector: 'img', classes: 'full-width' }
        ]
      },
      {
        title: 'Image Containers',
        items: [
          { title: 'Clear Wrap', block: 'div', classes: 'clearfix', wrapper: true }
        ]
      }
    ],
    images_upload_url: '',
    automatic_uploads: true,
    images_reuse_filename: false,
    paste_data_images: true,
    image_uploadtab: true,
    image_title: true,
    image_description: false,
    images_file_types: 'jpeg,jpg,png,gif,webp'
  };

  templateTypes = [
    { id: 'default', name: 'Standaard Pagina' },
    { id: 'posts', name: 'Posts Overzicht' },
    { id: 'contact', name: 'Contact Pagina' }
  ];

  categories: Category[] = [];

  availableComponents = [
    {
      id: 'title',
      name: 'Titel Sectie',
      description: 'Opvallende titel met subtitel en scheidingslijn',
      icon: 'heading'
    },
    {
      id: 'text',
      name: 'Tekst Component',
      description: 'Een component voor het invoeren van tekst',
      icon: 'document-text'
    },
    {
      id: 'products',
      name: 'Producten Grid/Lijst',
      description: 'Toon producten in een grid of lijst weergave',
      icon: 'cube'
    },
    {
      id: 'grid',
      name: 'Grid Layout',
      description: 'Flexibele grid met aanpasbare kolommen en items',
      icon: 'view-grid'
    },
    {
      id: 'media-gallery',
      name: 'Media Galerij',
      description: 'Toon afbeeldingen in diverse galerij layouts',
      icon: 'photograph'
    },
    {
      id: 'news',
      name: 'Nieuws/Blog',
      description: 'Toon nieuwsberichten of blog posts',
      icon: 'newspaper'
    },
    {
      id: 'projects',
      name: 'Projecten',
      description: 'Toon projecten in een portfolio weergave',
      icon: 'briefcase'
    },
    {
      id: 'form',
      name: 'Formulier',
      description: 'Configureerbaar contactformulier',
      icon: 'clipboard-list'
    },
    {
      id: 'map',
      name: 'Kaart',
      description: 'OpenStreetMap kaart met markers',
      icon: 'map-pin'
    },
    {
      id: 'action',
      name: 'Action Element',
      description: 'Button, banner of CTA sectie',
      icon: 'cursor-click'
    },
    {
      id: 'html-block',
      name: 'HTML Blok',
      description: 'Ruw HTML/CSS/JavaScript blok voor custom widgets en embeds',
      icon: 'code'
    }
  ];

  selectedComponentIndex: number | null = null;
  leftSidebarTab: 'components' | 'layers' = 'layers';

  /** Stappen voor de eenmalige rondleiding door de page-editor.
   *  Elke gebruiker ziet deze automatisch één keer (localStorage-gestuurd)
   *  en kan hem herstarten via het "?"-knopje rechtsonder. */
  onboardingSteps: TourStep[] = [
    {
      title: 'Welkom in de page editor',
      content: 'We nemen je snel mee langs de belangrijkste onderdelen. Gebruik de pijltjes op je toetsenbord of de knoppen hieronder om verder te gaan.',
    },
    {
      target: '[data-tour="left-sidebar"]',
      placement: 'right',
      title: 'Layers en componenten',
      content: 'Links zie je alle blokken die op deze pagina staan. Klik een blok aan om het te selecteren en te bewerken.',
      onEnter: () => { this.leftSidebarTab = 'layers'; },
    },
    {
      target: '[data-tour="components-tab-btn"]',
      placement: 'right',
      title: 'Nieuwe blokken toevoegen',
      content: 'Wissel naar het tabblad Componenten om nieuwe blokken toe te voegen: tekst, formulieren, een kaart of een custom HTML-blok.',
      onEnter: () => { this.leftSidebarTab = 'components'; },
    },
    {
      target: '[data-tour="preview-area"]',
      placement: 'left',
      title: 'Live preview',
      content: 'Dit is een directe preview van de pagina. Wijzigingen die je maakt zie je hier meteen terug.',
    },
    {
      target: '[data-tour="top-actions"]',
      placement: 'bottom',
      title: 'Opslaan en bekijken',
      content: 'Sla je wijzigingen op met de blauwe knop. "Bekijk preview" opent de pagina op de live site met cache-bypass zodat je het meteen goed ziet.',
    },
    {
      title: 'Je bent klaar!',
      content: 'Klik het "?"-knopje rechtsonder om deze rondleiding later opnieuw te openen. Veel plezier!',
    },
  ];
  showDeleteConfirmModal = false;
  componentToDeleteIndex: number | null = null;
  showMediaSelectorModal = false;
  showProductSelectorModal = false;
  showProjectSelectorModal = false;
  showTextEditorModal = false;

  // Grid cell management
  selectedGridCellIndex: number | null = null;
  selectedCellComponentIndex: number | null = null;
  editingCellComponent = false;
  showPaddingEditor = false;
  showMarginEditor = false;
  showCellPaddingEditor = false;
  showCellMarginEditor = false;
  mediaSelectorTarget: 'background' | 'gallery' | 'gridCell' = 'background';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private websiteService: WebsiteService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    // Subscribe to route params
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(async params => {
        this.pageId = params['paginanaam'];
        if (this.pageId) {
          await this.loadPageById(this.pageId);
        }
      });

    // Subscribe to global language changes
    this.websiteService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async lang => {
        this.currentLocale = lang;
        if (this.pageId) {
          await this.loadPageById(this.pageId);
        }
      });

    // Subscribe to domain changes
    this.websiteService.domainChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if (this.pageId) {
          await this.loadPageById(this.pageId);
        }
      });

    await this.loadWebsiteSettings();
  }

  ngOnDestroy() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadPageById(pageId: string) {
    try {
      const currentDomain = this.websiteService.getCurrentDomain();
      if (!currentDomain) {
        this.notificationService.show({
          message: 'No domain selected',
          type: 'error'
        });
        return;
      }

      // Load page by document ID
      const page = await this.websiteService.getPageById(pageId);

      if (page && page.domain === currentDomain) {
        this.currentPage = page;
        this.selectedCategories = page.categories || [];
        
        // Initialize components array if it doesn't exist
        if (!this.currentPage.components) {
          this.currentPage.components = [];
        }
        
        this.initializeTemplateType();
        await this.loadWebsiteSettings();
        await this.loadCategories();
      } else {
        this.currentPage = null;
        this.notificationService.show({
          message: 'Pagina niet gevonden',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading page:', error);
      this.notificationService.show({
        message: 'Pagina laden mislukt',
        type: 'error'
      });
    }
  }

  private async loadWebsiteSettings(): Promise<void> {
    const currentDomain = this.websiteService.getCurrentDomain();
    if (!currentDomain) return;

    const settings = await this.websiteService.getWebsiteSettings(currentDomain);
    if (settings) {
      this.availableLanguages = settings.availableLanguages || [];
      // Gebruik de opgeslagen taal als die beschikbaar is, anders de default
      const storedLang = this.websiteService.getCurrentLanguage();
      const availableCodes = this.availableLanguages.map(l => l.code);
      if (availableCodes.includes(storedLang)) {
        this.currentLocale = storedLang;
      } else {
        this.currentLocale = (settings.defaultLanguage as SupportedLanguage) || 'en';
      }
    }
  }

  private generateRoute(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  async updatePageTitle(newTitle: string) {
    if (!this.currentPage) return;
    
    try {
      this.currentPage.title = newTitle;
      
      // Auto-generate route from title if route is empty
      if (!this.currentPage.route) {
        this.currentPage.route = this.generateRoute(newTitle);
      }
      
      await this.websiteService.updatePage({
        ...this.currentPage,
        updatedAt: new Date()
      });
      
      this.notificationService.show({
        message: 'Titel succesvol bijgewerkt',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating title:', error);
      this.notificationService.show({
        message: 'Titel bijwerken mislukt',
        type: 'error'
      });
    }
  }

  async updatePageRoute(newRoute: string) {
    if (!this.currentPage) return;
    
    try {
      const cleanRoute = newRoute
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      this.currentPage.route = cleanRoute;
      
      await this.websiteService.updatePage({
        ...this.currentPage,
        updatedAt: new Date()
      });
      
      // Route is updated but we stay on the same page (ID doesn't change)
      
      this.notificationService.show({
        message: 'URL succesvol bijgewerkt',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating URL:', error);
      this.notificationService.show({
        message: 'URL bijwerken mislukt',
        type: 'error'
      });
    }
  }

  getTemplateDescription(type: string | undefined): string {
    switch(type) {
      case 'posts':
        return 'Toont een lijst van blog posts of nieuwsitems met paginering';
      case 'contact':
        return 'Bevat een contactformulier en contactinformatie';
      case 'default':
      default:
        return 'Een standaard pagina met aanpasbare inhoud';
    }
  }

  async updateTemplateType(type: string) {
    if (!this.currentPage) return;
    
    try {
      await this.websiteService.updatePage({
        ...this.currentPage,
        templateType: type,
        updatedAt: new Date()
      });
      
      if (type === 'posts') {
        await this.loadCategories();
      }
      
      this.notificationService.show({
        message: 'Template type succesvol bijgewerkt',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating template type:', error);
      this.notificationService.show({
        message: 'Template type bijwerken mislukt',
        type: 'error'
      });
    }
  }

  private initializeTemplateType() {
    if (!this.currentPage) return;
    this.currentPage.templateType = this.currentPage.templateType || 'default';
  }

  private async loadCategories() {
    try {
      const currentDomain = this.websiteService.getCurrentDomain();
      if (!currentDomain) return;

      this.categories = await this.websiteService.getCategories(currentDomain);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }

  toggleCategory(categoryId: string) {
    const index = this.selectedCategories.indexOf(categoryId);
    if (index === -1) {
      this.selectedCategories.push(categoryId);
    } else {
      this.selectedCategories.splice(index, 1);
    }
    this.saveCategories();
  }

  async saveCategories() {
    if (!this.currentPage) return;

    try {
      await this.websiteService.updatePage({
        ...this.currentPage,
        categories: this.selectedCategories,
        updatedAt: new Date()
      });
      
      this.notificationService.show({
        message: 'Categorieën succesvol opgeslagen',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving categories:', error);
      this.notificationService.show({
        message: 'Categorieën opslaan mislukt',
        type: 'error'
      });
    }
  }

  async addComponent(type: string) {
    if (!this.currentPage) return;

    const componentType = this.availableComponents.find(c => c.id === type);
    if (!componentType) return;

    if (!this.currentPage.components) {
      this.currentPage.components = [];
    }

    let emptyData: ComponentData = {};
    switch (type) {
      case 'title':
        emptyData = {
          title: '',
          subtitle: '',
          subtitlePosition: 'below',
          titleTag: 'h2',
          titleSize: '3xl',
          titleWeight: 'bold',
          alignment: 'center',
          showDivider: true,
          dividerWidth: 64,
          dividerColor: '',
          paddingY: 48
        };
        break;
      case 'text':
        emptyData = {
          content: ''
        };
        break;
      case 'products':
        emptyData = {
          viewType: 'grid',
          columns: 3,
          limit: 0,
          showPrice: true,
          showDescription: true,
          heading: ''
        };
        break;
      case 'grid':
        emptyData = {
          columns: 3,
          rows: 1,
          gap: 16,
          gapUnit: 'px',
          gridLayout: 'equal',
          cells: [],
          itemPadding: 16,
          itemBorderRadius: 8,
          itemBackgroundColor: '#f9fafb',
          showItemBorder: true,
          itemBorderColor: '#e5e7eb',
          heading: ''
        };
        break;
      case 'media-gallery':
        emptyData = {
          layoutType: 'grid',
          columns: 3,
          gap: 8,
          images: [],
          showCaptions: true,
          captionPosition: 'below',
          imageFit: 'cover',
          imageHeight: 200,
          imageHeightUnit: 'px',
          aspectRatio: 'auto',
          enableLightbox: true,
          hoverEffect: 'zoom',
          imageBorderRadius: 8,
          shadowStyle: 'sm',
          lazyLoad: true,
          heading: ''
        };
        break;
      case 'news':
        emptyData = {
          layoutType: 'cards',
          columns: 3,
          limit: 6,
          selectedCategoryIds: [],
          showDate: true,
          showImage: true,
          showExcerpt: true,
          excerptLength: 150,
          showCategory: false,
          showReadMore: true,
          readMoreText: 'Lees meer',
          dateFormat: 'medium',
          imageHeight: 200,
          imagePosition: 'top',
          cardStyle: 'elevated',
          heading: ''
        };
        break;
      case 'projects':
        emptyData = {
          viewType: 'grid',
          columns: 3,
          showDescription: true,
          showCategory: true,
          selectedProjectIds: [],
          heading: ''
        };
        break;
      case 'form':
        emptyData = {
          formId: 'form-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
          heading: '',
          description: '',
          submitButtonText: 'Versturen',
          successMessage: 'Bedankt voor uw bericht! We nemen zo snel mogelijk contact met u op.',
          errorMessage: 'Er ging iets mis. Probeer het opnieuw.',
          fields: [
            { id: 'field-' + Date.now() + '-1', label: 'Naam', name: 'naam', type: 'text', required: true, placeholder: 'Uw naam', options: [], width: 'full' },
            { id: 'field-' + Date.now() + '-2', label: 'E-mail', name: 'email', type: 'email', required: true, placeholder: 'uw@email.com', options: [], width: 'full' },
            { id: 'field-' + Date.now() + '-3', label: 'Bericht', name: 'bericht', type: 'textarea', required: true, placeholder: 'Uw bericht', options: [], width: 'full' }
          ],
          notificationEmail: ''
        };
        break;
      case 'map':
        emptyData = {
          heading: '',
          subtitle: '',
          lat: 52.3676,
          lng: 4.9041,
          zoom: 13,
          mapHeight: 400,
          mapHeightUnit: 'px',
          borderRadius: 8,
          showZoomControl: true,
          scrollWheelZoom: false,
          markers: []
        };
        break;
      case 'html-block':
        emptyData = {
          html: '',
          wrapperClass: '',
          paddingY: 0
        };
        break;
      case 'action':
        emptyData = {
          variant: 'button',
          text: 'Klik hier',
          link: '',
          openInNewTab: false,
          alignment: 'center',
          secondaryText: '',
          secondaryLink: '',
          secondaryOpenInNewTab: false,
          bannerTitle: '',
          bannerSubtitle: '',
          bannerBackgroundColor: '',
          bannerTextColor: '#ffffff',
          bannerBackgroundImage: '',
          bannerOverlayOpacity: 0.5,
          paddingY: 48,
          buttonSize: 'md',
          buttonStyle: 'primary'
        };
        break;
    }

    const newComponent: PageComponent = {
      type,
      data: emptyData
    };

    this.currentPage.components.push(newComponent);
    
    // Select the newly added component
    this.selectedComponentIndex = this.currentPage.components.length - 1;
    
    // Auto-save (silent)
    await this.savePage(false);
  }

  selectComponent(index: number) {
    this.selectedComponentIndex = index;
    this.editingCellComponent = false;
    this.scrollToComponentInPreview(index);
  }

  private scrollToComponentInPreview(index: number) {
    setTimeout(() => {
      const el = document.querySelector(`[data-component-index="${index}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }

  deselectComponent() {
    this.selectedComponentIndex = null;
  }

  getSelectedComponent(): PageComponent | null {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) {
      return null;
    }
    // When editing a grid cell component, return the cell component itself
    // so all existing sidebar editors work automatically
    if (this.editingCellComponent) {
      const cellComp = this.getSelectedCellComponent();
      if (cellComp) return cellComp;
    }
    return this.currentPage.components[this.selectedComponentIndex];
  }

  /** Returns the actual top-level component (even during cell editing) */
  getTopLevelComponent(): PageComponent | null {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) {
      return null;
    }
    return this.currentPage.components[this.selectedComponentIndex];
  }

  hasFullWidthCarousel(): boolean {
    if (!this.currentPage?.components) return false;
    return this.currentPage.components.some(comp => 
      comp.type === 'media-gallery' && 
      comp.data?.['layoutType'] === 'carousel' && 
      comp.data?.['fullWidth'] === true
    );
  }

  hoveredComponentIndex: number | null = null;


  removeComponent(index: number) {
    this.componentToDeleteIndex = index;
    this.showDeleteConfirmModal = true;
  }

  async confirmDeleteComponent() {
    if (this.componentToDeleteIndex === null || !this.currentPage?.components) {
      this.showDeleteConfirmModal = false;
      return;
    }

    const index = this.componentToDeleteIndex;
    this.currentPage.components.splice(index, 1);
    
    // Deselect if the removed component was selected
    if (this.selectedComponentIndex === index) {
      this.selectedComponentIndex = null;
    } else if (this.selectedComponentIndex !== null && this.selectedComponentIndex > index) {
      // Adjust selection index if a component before the selected one was removed
      this.selectedComponentIndex--;
    }
    
    this.showDeleteConfirmModal = false;
    this.componentToDeleteIndex = null;
    await this.savePage(false);
  }

  cancelDeleteComponent() {
    this.showDeleteConfirmModal = false;
    this.componentToDeleteIndex = null;
  }

  moveComponentUp(index: number) {
    if (!this.currentPage?.components || index === 0) return;
    
    // Swap with previous component
    const components = this.currentPage.components;
    [components[index - 1], components[index]] = [components[index], components[index - 1]];
    
    // Update selected index if needed
    if (this.selectedComponentIndex === index) {
      this.selectedComponentIndex = index - 1;
    } else if (this.selectedComponentIndex === index - 1) {
      this.selectedComponentIndex = index;
    }
    
    this.debouncedSave();
  }

  moveComponentDown(index: number) {
    if (!this.currentPage?.components || index === this.currentPage.components.length - 1) return;
    
    // Swap with next component
    const components = this.currentPage.components;
    [components[index], components[index + 1]] = [components[index + 1], components[index]];
    
    // Update selected index if needed
    if (this.selectedComponentIndex === index) {
      this.selectedComponentIndex = index + 1;
    } else if (this.selectedComponentIndex === index + 1) {
      this.selectedComponentIndex = index;
    }
    
    this.debouncedSave();
  }

  canMoveUp(index: number): boolean {
    return index > 0;
  }

  canMoveDown(index: number): boolean {
    if (!this.currentPage?.components) return false;
    return index < this.currentPage.components.length - 1;
  }

  updateTextComponentField(field: string, value: any) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    // When editing a grid cell component, update the cell component
    const component = this.editingCellComponent
      ? this.getSelectedCellComponent()
      : this.currentPage.components[this.selectedComponentIndex];
    if (!component) return;
    component.data[field] = value;

    // Debounce save
    this.debouncedSave();
  }

  openMediaSelector() {
    this.showMediaSelectorModal = true;
  }

  closeMediaSelector() {
    this.showMediaSelectorModal = false;
  }

  onMediaSelected(url: string) {
    if (this.mediaSelectorTarget === 'background') {
      this.updateTextComponentField('backgroundImage', url);
    } else if (this.mediaSelectorTarget === 'gallery') {
      // Add to gallery images
      if (this.selectedComponentIndex === null || !this.currentPage?.components) {
        this.closeMediaSelector();
        return;
      }

      const component = this.editingCellComponent
        ? this.getSelectedCellComponent()
        : this.currentPage.components[this.selectedComponentIndex];
      if (component && component.type === 'media-gallery') {
        const images = component.data['images'] || [];
        const newImage: any = {
          url: url,
          alt: ''
        };
        // Add carousel-specific fields if layout is carousel
        if (component.data['layoutType'] === 'carousel') {
          newImage.title = '';
          newImage.description = '';
          newImage.ctaButtons = [];
        } else {
          newImage.caption = '';
        }
        // Update images in-place (works for both top-level and cell components)
        component.data['images'] = [...images, newImage];
        this.debouncedSave();
      }
    } else if (this.mediaSelectorTarget === 'gridCell') {
      // Add to grid cell's media-gallery component
      const cellComponent = this.getSelectedCellComponent();
      if (cellComponent && cellComponent.type === 'media-gallery') {
        if (!cellComponent.data['images']) {
          cellComponent.data['images'] = [];
        }
        // Create new component reference for change detection
        const newComp: PageComponent = { 
          ...cellComponent, 
          data: { 
            ...cellComponent.data,
            images: [...(cellComponent.data['images'] || []), (() => {
              const newImage: any = {
                url: url,
                alt: ''
              };
              // Add carousel-specific fields if layout is carousel
              if (cellComponent.data['layoutType'] === 'carousel') {
                newImage.title = '';
                newImage.description = '';
                newImage.ctaButtons = [];
              } else {
                newImage.caption = '';
              }
              return newImage;
            })()]
          } 
        };
        
        // Update the grid cell component immutably
        if (this.selectedComponentIndex === null || this.selectedGridCellIndex === null || this.selectedCellComponentIndex === null || !this.currentPage?.components) {
          this.closeMediaSelector();
          return;
        }

        const grid = this.currentPage.components[this.selectedComponentIndex];
        if (grid.type !== 'grid' || !grid.data['cells']) {
          this.closeMediaSelector();
          return;
        }

        const cells = grid.data['cells'] as { id?: string; components?: PageComponent[] }[];
        const cell = cells[this.selectedGridCellIndex];
        if (!cell?.components) {
          this.closeMediaSelector();
          return;
        }

        const newComps = cell.components.slice();
        newComps[this.selectedCellComponentIndex] = newComp;
        const newCell = { ...cell, components: newComps };
        const newCells = cells.map((c, i) => i === this.selectedGridCellIndex ? newCell : c);
        const newGrid: PageComponent = { ...grid, data: { ...grid.data, cells: newCells } };
        this.currentPage.components[this.selectedComponentIndex] = newGrid;
        
        this.debouncedSave();
      }
    }
    this.closeMediaSelector();
    this.mediaSelectorTarget = 'background';
  }

  openProductSelector() {
    this.showProductSelectorModal = true;
  }

  closeProductSelector() {
    this.showProductSelectorModal = false;
  }

  onProductsSelected(productIds: string[]) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;
    
    // Create a new component object to trigger Angular's change detection
    const oldComponent = this.currentPage.components[this.selectedComponentIndex];
    const newComponent: PageComponent = {
      ...oldComponent,
      data: {
        ...oldComponent.data,
        selectedProductIds: productIds
      }
    };
    
    // Replace the component with the new reference
    this.currentPage.components[this.selectedComponentIndex] = newComponent;
    this.closeProductSelector();
    this.debouncedSave();
  }

  onRemoveProduct(productId: string, componentIndex: number) {
    if (!this.currentPage?.components) return;
    
    const component = this.currentPage.components[componentIndex];
    if (component.type !== 'products') return;
    
    const currentIds: string[] = component.data['selectedProductIds'] || [];
    const newIds = currentIds.filter(id => id !== productId);
    
    // Create a new component object to trigger Angular's change detection
    const newComponent: PageComponent = {
      ...component,
      data: {
        ...component.data,
        selectedProductIds: newIds
      }
    };
    
    // Replace the component with the new reference
    this.currentPage.components[componentIndex] = newComponent;
    this.debouncedSave();
  }

  // Project selector methods
  openProjectSelector() {
    this.showProjectSelectorModal = true;
  }

  closeProjectSelector() {
    this.showProjectSelectorModal = false;
  }

  onProjectsSelected(projectIds: string[]) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;
    
    // Create a new component object to trigger Angular's change detection
    const oldComponent = this.currentPage.components[this.selectedComponentIndex];
    const newComponent: PageComponent = {
      ...oldComponent,
      data: {
        ...oldComponent.data,
        selectedProjectIds: projectIds
      }
    };
    
    // Replace the component with the new reference
    this.currentPage.components[this.selectedComponentIndex] = newComponent;
    this.closeProjectSelector();
    this.debouncedSave();
  }

  onRemoveProject(projectId: string, componentIndex: number) {
    if (!this.currentPage?.components) return;
    
    const component = this.currentPage.components[componentIndex];
    if (component.type !== 'projects') return;
    
    const currentIds: string[] = component.data['selectedProjectIds'] || [];
    const newIds = currentIds.filter(id => id !== projectId);
    
    // Create a new component object to trigger Angular's change detection
    const newComponent: PageComponent = {
      ...component,
      data: {
        ...component.data,
        selectedProjectIds: newIds
      }
    };
    
    // Replace the component with the new reference
    this.currentPage.components[componentIndex] = newComponent;
    this.debouncedSave();
  }

  onEditorChange(content: string) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;
    
    const component = this.currentPage.components[this.selectedComponentIndex];
    component.data['content'] = content;
    
    // Debounce save to avoid too many saves
    this.debouncedSave();
  }

  debouncedSave() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    this.saveTimer = setTimeout(() => {
      this.savePage(false); // Silent save, no notification
    }, 1000); // Save after 1 second of inactivity
  }

  async moveComponent(fromIndex: number, toIndex: number) {
    if (!this.currentPage?.components) return;

    const component = this.currentPage.components[fromIndex];
    this.currentPage.components.splice(fromIndex, 1);
    this.currentPage.components.splice(toIndex, 0, component);
    
    // Update selection index
    if (this.selectedComponentIndex === fromIndex) {
      this.selectedComponentIndex = toIndex;
    } else if (this.selectedComponentIndex !== null) {
      if (fromIndex < this.selectedComponentIndex && toIndex >= this.selectedComponentIndex) {
        this.selectedComponentIndex--;
      } else if (fromIndex > this.selectedComponentIndex && toIndex <= this.selectedComponentIndex) {
        this.selectedComponentIndex++;
      }
    }
    
    await this.savePage(false);
  }

  getComponentInfo(type: string) {
    const component = this.availableComponents.find(c => c.id === type);
    return {
      name: component?.name || '',
      description: component?.description || '',
      icon: component?.icon || 'cube'
    };
  }

  async savePage(showNotification: boolean = true) {
    if (!this.currentPage) return;

    try {
      await this.websiteService.updatePage({
        ...this.currentPage,
        updatedAt: new Date()
      });
      
      if (showNotification) {
        const message = this.currentPage.templateType === 'posts' 
          ? 'Pagina en categorieën succesvol opgeslagen'
          : 'Pagina succesvol opgeslagen';
        
        this.notificationService.show({
          message,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error saving page:', error);
      this.notificationService.show({
        message: 'Pagina opslaan mislukt',
        type: 'error'
      });
    }
  }

  closeEditor() {
    this.router.navigate(['/pages']);
  }

  // Grid cell management methods
  onGridCellSelected(event: { cellIndex: number, componentIndex?: number }) {
    this.selectedGridCellIndex = event.cellIndex;
    this.selectedCellComponentIndex = event.componentIndex ?? null;
    this.scrollSidebarToGrid();
  }

  onComponentInCellSelected(event: { cellIndex: number, componentIndex: number }) {
    this.selectedGridCellIndex = event.cellIndex;
    this.selectedCellComponentIndex = event.componentIndex;
    this.scrollSidebarToGrid();
  }

  private scrollSidebarToGrid() {
    setTimeout(() => {
      const sidebar = document.querySelector('[data-sidebar-grid]');
      if (sidebar) {
        sidebar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  onAddComponentToGridCell(event: { cellIndex: number, componentType: string }) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const gridComponent = this.currentPage.components[this.selectedComponentIndex];
    if (gridComponent.type !== 'grid') return;

    if (!gridComponent.data['cells']) {
      gridComponent.data['cells'] = [];
    }

    const cells = gridComponent.data['cells'];

    // Make sure the cell exists
    while (cells.length <= event.cellIndex) {
      cells.push({
        id: this.generateCellId(),
        components: []
      });
    }

    // Create the new component with default data
    let componentData: ComponentData = {};
    switch (event.componentType) {
      case 'title':
        componentData = { title: '', subtitle: '', titleTag: 'h2', titleSize: '3xl', titleWeight: 'bold', alignment: 'center', showDivider: true, dividerWidth: 64, paddingY: 32 };
        break;
      case 'text':
        componentData = { content: '' };
        break;
      case 'media-gallery':
        componentData = {
          layoutType: 'grid',
          columns: 2,
          gap: 4,
          images: [],
          showCaptions: false,
          enableLightbox: true,
          imageBorderRadius: 4
        };
        break;
      case 'products':
        componentData = {
          viewType: 'grid',
          columns: 2,
          limit: 4,
          showPrice: true,
          showDescription: false
        };
        break;
      case 'news':
        componentData = {
          layoutType: 'compact',
          limit: 3,
          showImage: false,
          showExcerpt: false,
          selectedCategoryIds: []
        };
        break;
      case 'projects':
        componentData = {
          viewType: 'grid',
          columns: 2,
          showDescription: true,
          showCategory: true,
          selectedProjectIds: [],
          heading: ''
        };
        break;
      case 'form':
        componentData = {
          formId: 'form-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
          heading: '',
          description: '',
          submitButtonText: 'Versturen',
          successMessage: 'Bedankt voor uw bericht!',
          errorMessage: 'Er ging iets mis. Probeer het opnieuw.',
          fields: [
            { id: 'field-' + Date.now() + '-1', label: 'Naam', name: 'naam', type: 'text', required: true, placeholder: 'Uw naam', options: [], width: 'full' },
            { id: 'field-' + Date.now() + '-2', label: 'E-mail', name: 'email', type: 'email', required: true, placeholder: 'uw@email.com', options: [], width: 'full' },
            { id: 'field-' + Date.now() + '-3', label: 'Bericht', name: 'bericht', type: 'textarea', required: true, placeholder: 'Uw bericht', options: [], width: 'full' }
          ],
          notificationEmail: ''
        };
        break;
      case 'map':
        componentData = {
          heading: '',
          lat: 52.3676,
          lng: 4.9041,
          zoom: 13,
          mapHeight: 250,
          mapHeightUnit: 'px',
          borderRadius: 8,
          showZoomControl: true,
          scrollWheelZoom: false,
          markers: []
        };
        break;
      case 'action':
        componentData = {
          variant: 'button',
          text: 'Klik hier',
          link: '',
          openInNewTab: false,
          alignment: 'center',
          secondaryText: '',
          secondaryLink: '',
          secondaryOpenInNewTab: false,
          bannerTitle: '',
          bannerSubtitle: '',
          bannerBackgroundColor: '',
          bannerTextColor: '#ffffff',
          bannerBackgroundImage: '',
          bannerOverlayOpacity: 0.5,
          paddingY: 48,
          buttonSize: 'md',
          buttonStyle: 'primary'
        };
        break;
    }

    const newComponent: PageComponent = {
      type: event.componentType,
      data: componentData
    };

    cells[event.cellIndex].components.push(newComponent);
    this.selectedGridCellIndex = event.cellIndex;
    this.selectedCellComponentIndex = cells[event.cellIndex].components.length - 1;
    this.debouncedSave();
  }

  addGridCell() {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const gridComponent = this.currentPage.components[this.selectedComponentIndex];
    if (gridComponent.type !== 'grid') return;

    if (!gridComponent.data['cells']) {
      gridComponent.data['cells'] = [];
    }

    gridComponent.data['cells'].push({
      id: this.generateCellId(),
      components: []
    });

    this.selectedGridCellIndex = gridComponent.data['cells'].length - 1;
    this.debouncedSave();
  }

  removeGridCell(cellIndex: number) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const gridComponent = this.currentPage.components[this.selectedComponentIndex];
    if (gridComponent.type !== 'grid' || !gridComponent.data['cells']) return;

    gridComponent.data['cells'].splice(cellIndex, 1);

    if (this.selectedGridCellIndex === cellIndex) {
      this.selectedGridCellIndex = null;
      this.selectedCellComponentIndex = null;
    } else if (this.selectedGridCellIndex !== null && this.selectedGridCellIndex > cellIndex) {
      this.selectedGridCellIndex--;
    }

    this.debouncedSave();
  }

  removeComponentFromCell(cellIndex: number, componentIndex: number) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const gridComponent = this.currentPage.components[this.selectedComponentIndex];
    if (gridComponent.type !== 'grid' || !gridComponent.data['cells']) return;

    const cell = gridComponent.data['cells'][cellIndex];
    if (!cell || !cell.components) return;

    cell.components.splice(componentIndex, 1);

    if (this.selectedCellComponentIndex === componentIndex) {
      this.selectedCellComponentIndex = null;
    } else if (this.selectedCellComponentIndex !== null && this.selectedCellComponentIndex > componentIndex) {
      this.selectedCellComponentIndex--;
    }

    this.debouncedSave();
  }

  getSelectedGridCell(): any {
    if (this.selectedComponentIndex === null || this.selectedGridCellIndex === null || !this.currentPage?.components) {
      return null;
    }

    const gridComponent = this.currentPage.components[this.selectedComponentIndex];
    if (gridComponent.type !== 'grid' || !gridComponent.data['cells']) return null;

    return gridComponent.data['cells'][this.selectedGridCellIndex];
  }

  getSelectedCellComponent(): PageComponent | null {
    const cell = this.getSelectedGridCell();
    if (!cell || this.selectedCellComponentIndex === null || !cell.components) {
      return null;
    }
    return cell.components[this.selectedCellComponentIndex];
  }

  updateCellComponentField(field: string, value: any) {
    const component = this.getSelectedCellComponent();
    if (!component) return;

    component.data[field] = value;
    this.debouncedSave();
  }

  selectCellComponent(index: number) {
    this.selectedCellComponentIndex = index;
  }

  openCellComponentEditor(index: number) {
    this.selectedCellComponentIndex = index;
    this.editingCellComponent = true;
  }

  closeCellComponentEditor() {
    this.editingCellComponent = false;
  }

  moveCellComponentUp(cellIndex: number, componentIndex: number) {
    if (componentIndex <= 0) return;
    const cell = this.getGridCellAt(cellIndex);
    if (!cell?.components) return;
    const temp = cell.components[componentIndex];
    cell.components[componentIndex] = cell.components[componentIndex - 1];
    cell.components[componentIndex - 1] = temp;
    if (this.selectedCellComponentIndex === componentIndex) {
      this.selectedCellComponentIndex = componentIndex - 1;
    }
    this.debouncedSave();
  }

  moveCellComponentDown(cellIndex: number, componentIndex: number) {
    const cell = this.getGridCellAt(cellIndex);
    if (!cell?.components || componentIndex >= cell.components.length - 1) return;
    const temp = cell.components[componentIndex];
    cell.components[componentIndex] = cell.components[componentIndex + 1];
    cell.components[componentIndex + 1] = temp;
    if (this.selectedCellComponentIndex === componentIndex) {
      this.selectedCellComponentIndex = componentIndex + 1;
    }
    this.debouncedSave();
  }

  private getGridCellAt(cellIndex: number) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return null;
    const grid = this.currentPage.components[this.selectedComponentIndex];
    if (grid?.type !== 'grid') return null;
    return grid.data['cells']?.[cellIndex] || null;
  }

  updateGridCellField(field: string, value: any) {
    const cell = this.getSelectedGridCell();
    if (!cell) return;

    cell[field] = value;
    this.debouncedSave();
  }

  getComponentBackgroundStyles(component: PageComponent): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const bgType = component.data['backgroundType'] || 'solid';

    if (bgType === 'solid') {
      styles['background-color'] = component.data['backgroundColor'] || '#ffffff';
    } else if (bgType === 'gradient') {
      const from = component.data['gradientFrom'] || '#ffffff';
      const to = component.data['gradientTo'] || '#ffffff';
      styles['background'] = `linear-gradient(to bottom, ${from}, ${to})`;
    } else if (bgType === 'image') {
      const bgImage = component.data['backgroundImage'];
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
        styles['background-repeat'] = 'no-repeat';
      }
    } else if (bgType === 'theme') {
      const accent = component.data['themeAccent'];
      const accentMap: { [key: string]: string } = {
        'primary': '#2563eb',
        'accent': '#0ea5e9',
        'primary-light': '#dbeafe',
        'accent-light': '#e0f2fe',
        'dark': '#111827',
        'light': '#f9fafb',
        'primary-gradient': 'linear-gradient(135deg, #2563eb, #0ea5e9)',
      };
      if (accent && accentMap[accent]) {
        if (accent === 'primary-gradient') {
          styles['background'] = accentMap[accent];
        } else {
          styles['background-color'] = accentMap[accent];
        }
      }
    }

    return styles;
  }

  getComponentTypeName(type: string): string {
    const names: { [key: string]: string } = {
      'title': 'Titel',
      'text': 'Tekst',
      'media-gallery': 'Media Galerij',
      'products': 'Producten',
      'news': 'Nieuws',
      'grid': 'Grid',
      'form': 'Formulier',
      'map': 'Kaart',
      'html-block': 'HTML Blok'
    };
    return names[type] || type;
  }

  toggleNewsCategoryFilter(categoryId: string) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const comp = this.getSelectedComponent();
    if (!comp || comp.type !== 'news') return;

    const ids = (comp.data['selectedCategoryIds'] || []) as string[];
    const next = ids.includes(categoryId) ? ids.filter(x => x !== categoryId) : [...ids, categoryId];
    const newComp: PageComponent = {
      ...comp,
      data: { ...comp.data, selectedCategoryIds: next }
    };
    this.currentPage.components[this.selectedComponentIndex] = newComp;
    this.debouncedSave();
  }

  toggleNewsCategoryFilterForCell(categoryId: string) {
    const grid = this.getSelectedComponent();
    const cellComp = this.getSelectedCellComponent();
    if (!grid || grid.type !== 'grid' || !cellComp || cellComp.type !== 'news') return;
    if (this.selectedGridCellIndex === null || this.selectedCellComponentIndex === null || !grid.data['cells']) return;
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const ids = (cellComp.data['selectedCategoryIds'] || []) as string[];
    const next = ids.includes(categoryId) ? ids.filter(x => x !== categoryId) : [...ids, categoryId];
    const newCellComp: PageComponent = { ...cellComp, data: { ...cellComp.data, selectedCategoryIds: next } };

    const cells = grid.data['cells'] as { id?: string; components?: PageComponent[] }[];
    const cell = cells[this.selectedGridCellIndex];
    if (!cell?.components) return;

    const newComps = cell.components.slice();
    newComps[this.selectedCellComponentIndex] = newCellComp;
    const newCell = { ...cell, components: newComps };
    const newCells = cells.map((c, i) => i === this.selectedGridCellIndex ? newCell : c);
    const newGrid: PageComponent = { ...grid, data: { ...grid.data, cells: newCells } };
    this.currentPage.components[this.selectedComponentIndex] = newGrid;
    this.debouncedSave();
  }

  private generateCellId(): string {
    return 'cell-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  // Enhanced media selector for gallery
  openMediaSelectorForGallery() {
    this.mediaSelectorTarget = 'gallery';
    this.showMediaSelectorModal = true;
  }

  openMediaSelectorForGridCell() {
    this.mediaSelectorTarget = 'gridCell';
    this.showMediaSelectorModal = true;
  }

  private imageFieldUpdateTimer: any = null;

  updateImageField(imageIndex: number, field: 'alt' | 'caption' | 'subtitle' | 'title' | 'description', value: string) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const component = this.currentPage.components[this.selectedComponentIndex];
    if (component && component.type === 'media-gallery') {
      const images = [...(component.data['images'] || [])];
      if (images[imageIndex]) {
        images[imageIndex] = { ...images[imageIndex], [field]: value };
        const newComponent: PageComponent = {
          ...component,
          data: {
            ...component.data,
            images: images
          }
        };
        this.currentPage.components[this.selectedComponentIndex] = newComponent;
        
        // Debounce save but don't trigger change detection immediately
        if (this.imageFieldUpdateTimer) {
          clearTimeout(this.imageFieldUpdateTimer);
        }
        this.imageFieldUpdateTimer = setTimeout(() => {
          this.debouncedSave();
        }, 500);
      }
    }
  }

  addCTAToImage(imageIndex: number) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const component = this.currentPage.components[this.selectedComponentIndex];
    if (component && component.type === 'media-gallery') {
      const images = [...(component.data['images'] || [])];
      if (images[imageIndex]) {
        const ctaButtons = [...(images[imageIndex]['ctaButtons'] || [])];
        ctaButtons.push({
          text: 'Button',
          url: '#',
          style: 'primary'
        });
        images[imageIndex] = { ...images[imageIndex], ctaButtons: ctaButtons };
        const newComponent: PageComponent = {
          ...component,
          data: {
            ...component.data,
            images: images
          }
        };
        this.currentPage.components[this.selectedComponentIndex] = newComponent;
        this.debouncedSave();
      }
    }
  }

  updateCTAField(imageIndex: number, ctaIndex: number, field: 'text' | 'url' | 'style', value: string) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const component = this.currentPage.components[this.selectedComponentIndex];
    if (component && component.type === 'media-gallery') {
      const images = [...(component.data['images'] || [])];
      if (images[imageIndex] && images[imageIndex]['ctaButtons']) {
        const ctaButtons = [...images[imageIndex]['ctaButtons']];
        if (ctaButtons[ctaIndex]) {
          ctaButtons[ctaIndex] = { ...ctaButtons[ctaIndex], [field]: value };
          images[imageIndex] = { ...images[imageIndex], ctaButtons: ctaButtons };
          const newComponent: PageComponent = {
            ...component,
            data: {
              ...component.data,
              images: images
            }
          };
          this.currentPage.components[this.selectedComponentIndex] = newComponent;
          
          if (this.imageFieldUpdateTimer) {
            clearTimeout(this.imageFieldUpdateTimer);
          }
          this.imageFieldUpdateTimer = setTimeout(() => {
            this.debouncedSave();
          }, 500);
        }
      }
    }
  }

  removeCTAFromImage(imageIndex: number, ctaIndex: number) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const component = this.currentPage.components[this.selectedComponentIndex];
    if (component && component.type === 'media-gallery') {
      const images = [...(component.data['images'] || [])];
      if (images[imageIndex] && images[imageIndex]['ctaButtons']) {
        const ctaButtons = [...images[imageIndex]['ctaButtons']];
        ctaButtons.splice(ctaIndex, 1);
        images[imageIndex] = { ...images[imageIndex], ctaButtons: ctaButtons };
        const newComponent: PageComponent = {
          ...component,
          data: {
            ...component.data,
            images: images
          }
        };
        this.currentPage.components[this.selectedComponentIndex] = newComponent;
        this.debouncedSave();
      }
    }
  }

  removeImageFromGallery(imageIndex: number) {
    if (this.selectedComponentIndex === null || !this.currentPage?.components) return;

    const component = this.currentPage.components[this.selectedComponentIndex];
    if (component && component.type === 'media-gallery') {
      const images = [...(component.data['images'] || [])];
      images.splice(imageIndex, 1);
      const newComponent: PageComponent = {
        ...component,
        data: {
          ...component.data,
          images: images
        }
      };
      this.currentPage.components[this.selectedComponentIndex] = newComponent;
      this.debouncedSave();
    }
  }

  trackByImageIndex(index: number, item: any): any {
    return index;
  }

  blurInput(event: Event): void {
    (event.target as HTMLInputElement)?.blur();
  }


  // Text editor modal for grid cell components
  cellComponentContent: string = '';

  openTextEditorModal() {
    const component = this.getSelectedCellComponent();
    if (component && component.type === 'text') {
      this.cellComponentContent = component.data['content'] || '';
      this.showTextEditorModal = true;
    }
  }

  onCellComponentContentChange(content: string) {
    this.cellComponentContent = content;
  }

  saveCellComponentContent() {
    if (this.selectedComponentIndex === null || this.selectedGridCellIndex === null || this.selectedCellComponentIndex === null || !this.currentPage?.components) return;

    const grid = this.currentPage.components[this.selectedComponentIndex];
    if (grid.type !== 'grid' || !grid.data['cells']) return;

    const cells = grid.data['cells'] as { id?: string; components?: PageComponent[] }[];
    const cell = cells[this.selectedGridCellIndex];
    if (!cell?.components) return;

    // Create new component reference for change detection
    const comp = cell.components[this.selectedCellComponentIndex];
    const newComp: PageComponent = { ...comp, data: { ...comp.data, content: this.cellComponentContent } };
    const newComps = cell.components.slice();
    newComps[this.selectedCellComponentIndex] = newComp;
    const newCell = { ...cell, components: newComps };
    const newCells = cells.map((c, i) => i === this.selectedGridCellIndex ? newCell : c);
    const newGrid: PageComponent = { ...grid, data: { ...grid.data, cells: newCells } };
    this.currentPage.components[this.selectedComponentIndex] = newGrid;
    
    this.showTextEditorModal = false;
    this.debouncedSave();
  }

  // ─── Form field management ───────────────────────────────────

  /** Return the active form component – either a top-level form or one nested in a grid cell. */
  private getActiveFormComponent(): PageComponent | null {
    const cell = this.getSelectedCellComponent();
    if (cell && cell.type === 'form') return cell;
    const comp = this.getSelectedComponent();
    if (comp && comp.type === 'form') return comp;
    return null;
  }

  addFormField() {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    if (!comp.data['fields']) comp.data['fields'] = [];
    const fields = comp.data['fields'] as any[];
    fields.push({
      id: 'field-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6),
      label: '',
      name: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
      width: 'full'
    });
    this.debouncedSave();
  }

  removeFormField(index: number) {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    const fields = comp.data['fields'] as any[];
    if (!fields) return;
    fields.splice(index, 1);
    this.debouncedSave();
  }

  moveFormFieldUp(index: number) {
    const comp = this.getActiveFormComponent();
    if (!comp || index <= 0) return;

    const fields = comp.data['fields'] as any[];
    [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
    this.debouncedSave();
  }

  moveFormFieldDown(index: number) {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    const fields = comp.data['fields'] as any[];
    if (index >= fields.length - 1) return;
    [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]];
    this.debouncedSave();
  }

  updateFormField(index: number, key: string, value: any) {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    const fields = comp.data['fields'] as any[];
    if (!fields || !fields[index]) return;
    fields[index][key] = value;

    // Auto-generate name from label
    if (key === 'label' && typeof value === 'string') {
      fields[index]['name'] = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '_')
        .trim();
    }
    this.debouncedSave();
  }

  addSelectOption(fieldIndex: number) {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    const fields = comp.data['fields'] as any[];
    if (!fields || !fields[fieldIndex]) return;
    if (!fields[fieldIndex].options) fields[fieldIndex].options = [];
    fields[fieldIndex].options.push('');
    this.debouncedSave();
  }

  removeSelectOption(fieldIndex: number, optionIndex: number) {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    const fields = comp.data['fields'] as any[];
    if (!fields || !fields[fieldIndex]?.options) return;
    fields[fieldIndex].options.splice(optionIndex, 1);
    this.debouncedSave();
  }

  updateSelectOption(fieldIndex: number, optionIndex: number, value: string) {
    const comp = this.getActiveFormComponent();
    if (!comp) return;

    const fields = comp.data['fields'] as any[];
    if (!fields || !fields[fieldIndex]?.options) return;
    fields[fieldIndex].options[optionIndex] = value;
    this.debouncedSave();
  }

  // ─── Map geocoding & marker management ──────────────────────

  mapAddressQuery = '';
  mapAddressLoading = false;
  markerAddressQueries: { [key: number]: string } = {};
  markerAddressLoading: { [key: number]: boolean } = {};

  async geocodeMapAddress() {
    const comp = this.getSelectedComponent();
    if (!comp || comp.type !== 'map' || !this.mapAddressQuery.trim()) return;

    this.mapAddressLoading = true;
    try {
      const query = encodeURIComponent(this.mapAddressQuery.trim());
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`);
      const results = await response.json();

      if (results && results.length > 0) {
        const result = results[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        comp.data['lat'] = lat;
        comp.data['lng'] = lng;

        // If no markers exist yet, add one at this location
        if (!comp.data['markers'] || (comp.data['markers'] as any[]).length === 0) {
          comp.data['markers'] = [{
            id: 'marker-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6),
            lat,
            lng,
            title: result.display_name?.split(',')[0] || '',
            description: '',
            popupOpen: true
          }];
        }

        this.debouncedSave();
        this.notificationService.show({ message: 'Adres gevonden en kaart gecentreerd', type: 'success' });
      } else {
        this.notificationService.show({ message: 'Adres niet gevonden', type: 'error' });
      }
    } catch {
      this.notificationService.show({ message: 'Geocoding mislukt', type: 'error' });
    }
    this.mapAddressLoading = false;
  }

  async geocodeMarkerAddress(markerIndex: number) {
    const comp = this.getSelectedComponent();
    if (!comp || comp.type !== 'map') return;

    const markers = comp.data['markers'] as any[];
    if (!markers || !markers[markerIndex]) return;

    const query = this.markerAddressQueries[markerIndex]?.trim();
    if (!query) return;

    this.markerAddressLoading[markerIndex] = true;
    try {
      const encoded = encodeURIComponent(query);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1`);
      const results = await response.json();

      if (results && results.length > 0) {
        const result = results[0];
        markers[markerIndex].lat = parseFloat(result.lat);
        markers[markerIndex].lng = parseFloat(result.lon);
        if (!markers[markerIndex].title) {
          markers[markerIndex].title = result.display_name?.split(',')[0] || '';
        }
        this.debouncedSave();
        this.notificationService.show({ message: 'Marker geplaatst op adres', type: 'success' });
      } else {
        this.notificationService.show({ message: 'Adres niet gevonden', type: 'error' });
      }
    } catch {
      this.notificationService.show({ message: 'Geocoding mislukt', type: 'error' });
    }
    this.markerAddressLoading[markerIndex] = false;
  }

  addMapMarker() {
    const comp = this.getSelectedComponent();
    if (!comp || comp.type !== 'map') return;

    if (!comp.data['markers']) comp.data['markers'] = [];
    const markers = comp.data['markers'] as any[];
    markers.push({
      id: 'marker-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6),
      lat: comp.data['lat'] || 52.3676,
      lng: comp.data['lng'] || 4.9041,
      title: '',
      description: '',
      popupOpen: false
    });
    this.debouncedSave();
  }

  removeMapMarker(index: number) {
    const comp = this.getSelectedComponent();
    if (!comp || comp.type !== 'map') return;

    const markers = comp.data['markers'] as any[];
    if (!markers) return;
    markers.splice(index, 1);
    this.debouncedSave();
  }

  updateMapMarker(index: number, key: string, value: any) {
    const comp = this.getSelectedComponent();
    if (!comp || comp.type !== 'map') return;

    const markers = comp.data['markers'] as any[];
    if (!markers || !markers[index]) return;

    if (key === 'lat' || key === 'lng') {
      markers[index][key] = parseFloat(value) || 0;
    } else {
      markers[index][key] = value;
    }
    this.debouncedSave();
  }
}
